import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AdHocAggregateResult, AdHocTrendResult, AnalyticsFilter, AnalyticsMetric } from '@/shared/analytics/AnalyticsTypes';
import { modelTier, type ModelTier } from './ModelTier';

const FIT_METRICS: AnalyticsMetric[] = ['turns', 'totalTokens', 'allocatedCostUsd'];
const TOTALS_METRICS: AnalyticsMetric[] = ['turns', 'totalTokens', 'allocatedCostUsd', 'users', 'avgModelFit'];
const USER_METRICS: AnalyticsMetric[] = ['avgModelFit', 'turns'];
/** Enough (user × model × difficulty) cells to cover the field at this scale. */
const USER_ROW_LIMIT = 2000;

export const DIFFICULTY_ORDER = ['low', 'medium', 'high'] as const;
export type Difficulty = (typeof DIFFICULTY_ORDER)[number];

export function isDifficulty(value: string | undefined): value is Difficulty {
    return value === 'low' || value === 'medium' || value === 'high';
}

/** A premium model fits high/medium work; a standard model fits low work. */
export function suitsTier(tier: ModelTier, difficulty: Difficulty): boolean {
    return tier === 'premium' ? difficulty !== 'low' : difficulty === 'low';
}

export interface ModelDifficultyCell {
    turns: number;
    totalTokens: number;
    cost: number;
}

export interface ModelFitRow {
    model: string;
    tier: ModelTier;
    classifiedTurns: number;
    totalTokens: number;
    cost: number;
    fitTurns: number;
    misfitTurns: number;
    misfitTokens: number;
    misfitCost: number;
    misfitShare: number;
    classifiedFitScore: number;
    byDifficulty: Record<Difficulty, ModelDifficultyCell>;
}

/** One person's model-fit footprint: how much of their work went to a tier-inappropriate model, and the premium spend that bought (waste). */
export interface ModelFitUserRow {
    userId: string;
    classifiedTurns: number;
    totalTokens: number;
    cost: number;
    fitTurns: number;
    misfitTurns: number;
    misfitShare: number;
    /** Allocated cost of this user's over-provisioned prompts (premium model on easy work) — the avoidable spend. */
    wasteCost: number;
    overTurns: number;
    underTurns: number;
    /** Average classified model-fit score for the user (1–100); 0 when unscored. */
    classifiedFitScore: number;
}

export interface FitTrendPoint {
    label: string;
    fitPct: number;
}

export interface ModelFitSnapshot {
    fitScore: number;
    fitScoreFromClassification: boolean;
    overProvisionedShare: number;
    underProvisionedShare: number;
    premiumShare: number;
    classifiedTurns: number;
    totalTurns: number;
    users: number;
    totalTokens: number;
    totalCost: number;
    premiumModelCount: number;
    standardModelCount: number;
    /** Waste: allocated cost of every tier-mismatched prompt (premium on easy, or standard on hard). */
    wasteCost: number;
    /** Allocated cost of under-provisioned prompts (standard model on hard work) — a quality-risk figure, not direct waste. */
    underCost: number;
    models: ModelFitRow[];
    userRows: ModelFitUserRow[];
    trend: FitTrendPoint[];
}

function emptyCell(): ModelDifficultyCell {
    return { turns: 0, totalTokens: 0, cost: 0 };
}

/**
 * Folds the model × difficulty aggregate into per-model fit rows: each turn is
 * "fit" when the model's tier suits the task difficulty (premium on hard/medium,
 * standard on easy), and "misfit" otherwise. Rows with no classified difficulty
 * are dropped. Returned sorted by token volume, highest first.
 */
function buildModelRows(matrix: AdHocAggregateResult): ModelFitRow[] {
    const byModel = new Map<string, ModelFitRow>();
    for (const row of matrix.rows) {
        const model = row.keys.model ?? '—';
        const difficulty = row.keys.difficulty;
        if (!isDifficulty(difficulty)) continue;
        const tier = modelTier(model);
        const existing =
            byModel.get(model) ??
            {
                model,
                tier,
                classifiedTurns: 0,
                totalTokens: 0,
                cost: 0,
                fitTurns: 0,
                misfitTurns: 0,
                misfitTokens: 0,
                misfitCost: 0,
                misfitShare: 0,
                classifiedFitScore: 0,
                byDifficulty: { low: emptyCell(), medium: emptyCell(), high: emptyCell() },
            };
        const turns = row.metrics.turns;
        const tokens = row.metrics.totalTokens;
        const cost = row.metrics.allocatedCostUsd;
        existing.classifiedTurns += turns;
        existing.totalTokens += tokens;
        existing.cost += cost;
        existing.byDifficulty[difficulty] = { turns, totalTokens: tokens, cost };
        if (suitsTier(tier, difficulty)) {
            existing.fitTurns += turns;
        } else {
            existing.misfitTurns += turns;
            existing.misfitTokens += tokens;
            existing.misfitCost += cost;
        }
        byModel.set(model, existing);
    }
    const rows = [...byModel.values()];
    for (const row of rows) row.misfitShare = row.classifiedTurns > 0 ? row.misfitTurns / row.classifiedTurns : 0;
    return rows.sort((a, b) => b.totalTokens - a.totalTokens);
}

/**
 * Folds the user × model × difficulty aggregate into per-user fit rows: each turn
 * is "fit" when the model's tier suits the difficulty, else "misfit" — split into
 * over-provisioned (premium on easy, whose cost is waste) and under-provisioned
 * (standard on hard). Users with no classified difficulty are dropped.
 */
function buildUserRows(matrix: AdHocAggregateResult): Map<string, ModelFitUserRow> {
    const byUser = new Map<string, ModelFitUserRow>();
    for (const row of matrix.rows) {
        const userId = row.keys.user_id ?? '—';
        const difficulty = row.keys.difficulty;
        if (!isDifficulty(difficulty)) continue;
        const tier = modelTier(row.keys.model ?? '—');
        const turns = row.metrics.turns;
        const tokens = row.metrics.totalTokens;
        const cost = row.metrics.allocatedCostUsd;
        const entry =
            byUser.get(userId) ??
            { userId, classifiedTurns: 0, totalTokens: 0, cost: 0, fitTurns: 0, misfitTurns: 0, misfitShare: 0, wasteCost: 0, overTurns: 0, underTurns: 0, classifiedFitScore: 0 };
        entry.classifiedTurns += turns;
        entry.totalTokens += tokens;
        entry.cost += cost;
        if (suitsTier(tier, difficulty)) {
            entry.fitTurns += turns;
        } else {
            entry.misfitTurns += turns;
            entry.wasteCost += cost;
            if (tier === 'premium') entry.overTurns += turns;
            else entry.underTurns += turns;
        }
        byUser.set(userId, entry);
    }
    for (const entry of byUser.values()) entry.misfitShare = entry.classifiedTurns > 0 ? entry.misfitTurns / entry.classifiedTurns : 0;
    return byUser;
}

/**
 * Backs the Behavior › Model Fit page: whether work is going to the right-sized
 * model. Reads the model × difficulty distribution from the analytics gateway,
 * classifies each model's tier by name, and scores tier-vs-difficulty fit the
 * same way the Insights model-fit metric does.
 */
@injectable()
export class BehaviorModelFitViewModel extends BaseViewModel {
    snapshot: ModelFitSnapshot | null = null;
    loading = false;
    errorMessage: string | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway) {
        super();
    }

    async load(filters: AnalyticsFilter[]): Promise<void> {
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const [primary, matrix, userMatrix, userPrimary] = await Promise.all([
                this.gateway.aggregate({ source: 'interactions', groupBy: ['model'], metrics: TOTALS_METRICS, orderBy: { metric: 'totalTokens', direction: 'desc' }, filters }),
                this.gateway.aggregate({ source: 'interactions', groupBy: ['model', 'difficulty'], metrics: FIT_METRICS, filters }),
                this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'model', 'difficulty'], metrics: FIT_METRICS, filters, limit: USER_ROW_LIMIT }),
                this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id'], metrics: USER_METRICS, filters, limit: USER_ROW_LIMIT }),
            ]);
            const models = buildModelRows(matrix);
            const classifiedFitByModel = new Map(primary.rows.map((row) => [row.keys.model ?? '—', row.metrics.avgModelFit]));
            for (const model of models) model.classifiedFitScore = Math.round(classifiedFitByModel.get(model.model) ?? 0);

            const userMap = buildUserRows(userMatrix);
            const classifiedFitByUser = new Map(userPrimary.rows.map((row) => [row.keys.user_id ?? '—', row.metrics.avgModelFit]));
            for (const user of userMap.values()) user.classifiedFitScore = Math.round(classifiedFitByUser.get(user.userId) ?? 0);
            const users = [...userMap.values()].sort((a, b) => b.wasteCost - a.wasteCost || b.misfitTurns - a.misfitTurns);

            // Waste = the allocated cost of every tier-mismatched prompt (premium on
            // easy, or standard on hard); underCost isolates the standard-on-hard slice.
            let wasteCost = 0;
            let underCost = 0;
            for (const model of models) {
                wasteCost += model.misfitCost;
                if (model.tier === 'standard') underCost += model.misfitCost;
            }

            let fitTurns = 0;
            let overTurns = 0;
            let underTurns = 0;
            let classifiedTurns = 0;
            let premiumTurns = 0;
            for (const model of models) {
                fitTurns += model.fitTurns;
                classifiedTurns += model.classifiedTurns;
                if (model.tier === 'premium') {
                    premiumTurns += model.classifiedTurns;
                    overTurns += model.misfitTurns;
                } else {
                    underTurns += model.misfitTurns;
                }
            }

            // The classified per-interaction score is authoritative; the tier×difficulty
            // ratio is a transitional fallback until the pass has scored this window.
            const classifiedFitAvg = primary.totals.avgModelFit;
            const heuristicFitScore = classifiedTurns > 0 ? Math.round((fitTurns / classifiedTurns) * 100) : 0;

            const trend = await this.loadTrend(models, filters);

            this.snapshot = {
                fitScore: classifiedFitAvg > 0 ? Math.round(classifiedFitAvg) : heuristicFitScore,
                fitScoreFromClassification: classifiedFitAvg > 0,
                overProvisionedShare: classifiedTurns > 0 ? overTurns / classifiedTurns : 0,
                underProvisionedShare: classifiedTurns > 0 ? underTurns / classifiedTurns : 0,
                premiumShare: classifiedTurns > 0 ? premiumTurns / classifiedTurns : 0,
                classifiedTurns,
                totalTurns: primary.totals.turns,
                users: primary.totals.users,
                totalTokens: primary.totals.totalTokens,
                totalCost: primary.totals.allocatedCostUsd,
                premiumModelCount: models.filter((model) => model.tier === 'premium').length,
                standardModelCount: models.filter((model) => model.tier === 'standard').length,
                wasteCost,
                underCost,
                models,
                userRows: users,
                trend,
            };
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
            this.snapshot = null;
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    /**
     * Weekly fit percentage. Fit needs both a tier and a difficulty per bucket,
     * which one grouped trend can't express, so we run one difficulty-bucketed
     * trend filtered to premium models and one to standard, then fold both into a
     * single fit-over-classified series.
     */
    private async loadTrend(models: ModelFitRow[], filters: AnalyticsFilter[]): Promise<FitTrendPoint[]> {
        const premiumModels = models.filter((model) => model.tier === 'premium').map((model) => model.model);
        const standardModels = models.filter((model) => model.tier === 'standard').map((model) => model.model);
        const [premiumTrend, standardTrend] = await Promise.all([
            premiumModels.length > 0 ? this.trendByDifficulty(filters, premiumModels) : Promise.resolve(null),
            standardModels.length > 0 ? this.trendByDifficulty(filters, standardModels) : Promise.resolve(null),
        ]);

        const buckets = new Map<string, { fit: number; classified: number }>();
        const fold = (result: AdHocTrendResult | null, tier: ModelTier): void => {
            if (result === null) return;
            for (const point of result.points) {
                const difficulty = point.key;
                if (!isDifficulty(difficulty)) continue;
                const turns = point.metrics.turns;
                const entry = buckets.get(point.bucket) ?? { fit: 0, classified: 0 };
                entry.classified += turns;
                if (suitsTier(tier, difficulty)) entry.fit += turns;
                buckets.set(point.bucket, entry);
            }
        };
        fold(premiumTrend, 'premium');
        fold(standardTrend, 'standard');

        return [...buckets.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([bucket, value]) => ({ label: bucket, fitPct: value.classified > 0 ? Math.round((value.fit / value.classified) * 100) : 0 }));
    }

    private trendByDifficulty(filters: AnalyticsFilter[], models: string[]): Promise<AdHocTrendResult> {
        return this.gateway.trend({
            source: 'interactions',
            bucket: 'week',
            metrics: ['turns'],
            groupBy: 'difficulty',
            filters: [...filters, { field: 'model', values: models }],
        });
    }
}
